// frontend/src/components/Selector/Selector.jsx

import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AnalysisContext } from '../../contexts/AnalysisContext';
import { AuthContext } from '../../contexts/AuthContext';
import { UIContext } from '../../contexts/UIContext';
import './Selector.css';
import { 
    FormControl, InputLabel, Select, MenuItem, Tooltip, IconButton, 
    Switch, Typography, Checkbox, ListItemText, Accordion, 
    AccordionSummary, AccordionDetails, Box, Button, Alert
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip as RechartsTooltip} from 'recharts';

// Matching your theme colors
const CHART_COLORS = {
    base: '#0088FE',       // Blue
    explanation: '#00C49F', // Teal
    regions: '#FFBB28'      // Yellow
};

// --- SYMBOL MAPPING ---
const CURRENCY_SYMBOLS = {
    'USD': '$',
    'EUR': '€',
    'GBP': '£',
    'JPY': '¥',
    'AUD': 'A$',
    'CAD': 'C$',
    'CHF': 'Fr',
    'CNH': '¥'
};

const FALLBACK_RATES = { USD: 1 };
// OPTIMISTIC UI FALLBACK: Default prices to prevent UI jumping while loading
const FALLBACK_PRICING = {
    prices: {
        analysis_new: { text: 40, image: 66, audio: 80, video: 198 },
        analysis_cached: { text: 4, image: 7, audio: 8, video: 20 },
        analysis_addons: { explanation: 25, regions: 25 }
    },
    billing_meta: { unit_to_currency_ratio: 0.001 }
};

const DEFAULT_PREFS = {
    default_content_type: 'image',
    displayed_content_types: ['image', 'text', 'video', 'audio'],
    default_regions: true,
    display_regions: true,
    default_explanation: true,
    display_explanation: true
};

// Helper: Format Currency
// Added 'decimals' parameter with default 3
const formatCurrency = (amount, currencyCode, decimals = 3) => {
    const symbol = CURRENCY_SYMBOLS[currencyCode] || currencyCode;
    const numberPart = new Intl.NumberFormat('en-US', {
        style: 'decimal',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }).format(amount);
    // Added a space for cleaner separation based on your preference ($ 0.23)
    return `${symbol} ${numberPart}`;
};


function Selector() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { openDocViewer, setIsOnboardingModalOpen } = useContext(UIContext);
    const {
        contentType,
        setContentType,
        setContent,
        setAnalysisResults,
        tasks,
        setTasks,
        includeRegions,
        setIncludeRegions,
        includeExplanation,
        setIncludeExplanation,
        setEstimatedCostUnits
    } = useContext(AnalysisContext);

    const [selectedCurrency, setSelectedCurrency] = useState(user?.preferred_currency || 'USD');
    const [isCostExpanded, setIsCostExpanded] = useState(false);

    // Provide Default Prefs
    const prefs = user?.analysis_preferences ? { ...DEFAULT_PREFS, ...user.analysis_preferences } : DEFAULT_PREFS;

    // --- Effect: Update currency if user preference changes (e.g. after editing profile) ---
    useEffect(() => {
        if (user?.preferred_currency) {
            setSelectedCurrency(user.preferred_currency);
        }
    }, [user?.preferred_currency]);

    // =========================================================================
    // TANSTACK QUERIES: Replaces manual useEffect fetch chains
    // =========================================================================

    // Fetch Tasks Config (Cached globally)
    const { data: tasksConfigData } = useQuery({
        queryKey: ['tasksConfig'],
        queryFn: async () => {
            const res = await fetch('/api/config/tasks');
            if (!res.ok) throw new Error('Failed to load tasks config');
            return res.json();
        },
        staleTime: Infinity, // Configuration rarely changes
    });
    const tasksConfig = tasksConfigData?.tasks || null;

    // Fetch Pricing & Rates (Cached globally)
    const { data: billingConfig } = useQuery({
        queryKey: ['billingConfig'],
        queryFn: async () => {
            const [priceRes, rateRes] = await Promise.all([
                fetchWithAuth('/api/config/billing'),
                fetchWithAuth('/api/user/billing?limit=1')
            ]);
            const prices = priceRes.ok ? await priceRes.json() : null;
            const rates = rateRes.ok ? await rateRes.json() : { currency_rates: { USD: 1 } };
            return { prices, rates: rates.currency_rates || { USD: 1 } };
        },
        enabled: !!user,
        staleTime: Infinity,
        placeholderData: { prices: FALLBACK_PRICING, rates: FALLBACK_RATES }
    });
    const pricingConfig = billingConfig?.prices || null;
    const currencyRates = billingConfig?.rates || FALLBACK_RATES;

    // --- DYNAMIC: Build Available Tasks List ---
    const availableTasks = useMemo(() => {
        // OPTIMISTIC UI FALLBACK: Prevent empty dropdown jump while loading
        if (!tasksConfig) {
            return tasks.map(key => {
                const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                return {
                    value: key,
                    label: t(`selector.analysisTasks.${key}`, { 
                        defaultValue: t(`selector.analysisTasks.${camelCaseKey}`, { defaultValue: key }) 
                    }),
                    description: ""
                };
            });
        }
        
        // Get the user's specific array of displayed tasks from the DB
        const userDisplayedTasks = user?.analysis_preferences?.displayed_tasks;
        const hasUserPrefs = Array.isArray(userDisplayedTasks) && userDisplayedTasks.length > 0;

        return Object.entries(tasksConfig)
            .filter(([key, config]) => !config.is_meta_task) // Hide meta-tasks like Vetra Score
            .filter(([key, config]) => {
                // FIX: Respect DB Preferences. If none exist yet, fallback to YAML's 'default_on_ui'
                if (hasUserPrefs) {
                    return userDisplayedTasks.includes(key);
                }
                return config.default_on_ui !== false;
            })
            .map(([key, config]) => {
                const camelCaseKey = key.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                
                // Title Translation mapping
                const possiblePaths = [
                    `selector.analysisTasks.${key}`,
                    `selector.analysisTasks.${camelCaseKey}`,
                    `analysisTasks.${key}`,
                    `analysisTasks.${camelCaseKey}`
                ];
                
                let finalLabel = config.display_name; // YAML Fallback
                for (let path of possiblePaths) {
                    const res = t(path);
                    if (res !== path) {
                        finalLabel = res;
                        break;
                    }
                }
                
                // Tooltip Translation mapping (Matches logic in AccountPage)
                const tooltipDescription = t(`verificationPage.scoreDefinitions.${key}`, config.description);
                
                return {
                    value: key,
                    label: finalLabel,
                    description: tooltipDescription
                };
            });
    }, [tasksConfig, tasks, t, user?.analysis_preferences?.displayed_tasks]);

    // --- DYNAMIC: Helper to get friendly task list string ---
    const getTasksLabel = useCallback(() => {
        if (!tasks || tasks.length === 0 || !tasksConfig) return "";
        const labels = tasks.map(task => {
            const camelCaseKey = task.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
            
            const possiblePaths = [
                `selector.analysisTasks.short.${task}`,
                `selector.analysisTasks.short.${camelCaseKey}`,
                `selector.analysisTasks.${task}`,
                `selector.analysisTasks.${camelCaseKey}`,
                `analysisTasks.${task}`,
                `analysisTasks.${camelCaseKey}`
            ];
            
            for (let path of possiblePaths) {
                const res = t(path);
                if (res !== path) return res;
            }
            
            // Ultimate fallback to YAML
            return tasksConfig?.[task]?.display_name || task;
        });
        return labels.join(', ');
    }, [tasks, tasksConfig, t]); // Dependencies for the function

    // --- DYNAMIC: Determine which content types support Regions across all active tasks ---
    const supportedRegionTypes = useMemo(() => {
        if (!tasksConfig) return [];
        const types = new Set();
        Object.values(tasksConfig).forEach(task => {
            if (!task.is_meta_task && task.supported_media) {
                Object.entries(task.supported_media).forEach(([mediaType, mediaConfig]) => {
                    if (mediaConfig.supports_regions) types.add(mediaType);
                });
            }
        });
        return Array.from(types);
    }, [tasksConfig]);

    // --- DYNAMIC: Is feature enabled for currently selected tasks & content type? ---
    const isRegionsFeatureEnabled = useMemo(() => {
        // OPTIMISTIC FALLBACK: Keep switch ON while loading OR before default tasks populate
        if (!tasksConfig || tasks.length === 0) return true; 
        
        // True if ANY selected task supports regions for the current content type
        return tasks.some(taskKey => tasksConfig[taskKey]?.supported_media?.[contentType]?.supports_regions);
    }, [tasksConfig, tasks, contentType]);

    const isExplanationFeatureEnabled = useMemo(() => {
        // OPTIMISTIC FALLBACK: Keep switch ON while loading OR before default tasks populate
        if (!tasksConfig || tasks.length === 0) return true; 
        
        // True if ANY selected task supports explanations for the current content type
        return tasks.some(taskKey => tasksConfig[taskKey]?.supported_media?.[contentType]?.supports_explanation);
    }, [tasksConfig, tasks, contentType]);

    // --- Calculation Logic ---
    const costData = useMemo(() => {
        if (!pricingConfig || tasks.length === 0) return null;

        const prices = pricingConfig.prices;
        const taskCount = tasks.length;
        
        // Cost calculations
        const baseUnitCost = prices.analysis_new[contentType] || 0;
        const totalBaseCost = baseUnitCost * taskCount;

        const explanationUnitCost = prices.analysis_addons.explanation || 0;
        const totalExplanationCost = includeExplanation ? (explanationUnitCost * taskCount) : 0;

        const regionsUnitCost = prices.analysis_addons.regions || 0;
        const supportsRegions = ['image', 'video', 'text'].includes(contentType);
        const totalRegionsCost = (includeRegions && supportsRegions) ? (regionsUnitCost * taskCount) : 0;

        const totalUnits = totalBaseCost + totalExplanationCost + totalRegionsCost;
        
        // Rate conversions
        const rate = currencyRates[selectedCurrency] || 1;
        const currencyRatio = pricingConfig.billing_meta.unit_to_currency_ratio || 0.001;
        const totalFiatValue = totalUnits * currencyRatio * rate;
        const totalFiatDisplay = formatCurrency(totalFiatValue, selectedCurrency, 2);

        const taskNamesString = getTasksLabel(); 
        const baseAnalysisName = t('selector.costEstimator.baseAnalysisLabel', { 
            count: taskCount, 
            tasks: taskNamesString 
        });

        // Helper to calc fiat string for each item
        const getFiatStr = (units) => formatCurrency(units * currencyRatio * rate, selectedCurrency, 3);

        // Chart Data Structure
        const chartData = [
            { 
                name: baseAnalysisName,
                value: totalBaseCost, 
                color: CHART_COLORS.base,
                fiat: getFiatStr(totalBaseCost)
            },
        ];

        if (totalExplanationCost > 0) {
            chartData.push({ 
                name: t('selector.costEstimator.explanation'), 
                value: totalExplanationCost, 
                color: CHART_COLORS.explanation,
                fiat: getFiatStr(totalExplanationCost)
            });
        }
        if (totalRegionsCost > 0) {
            chartData.push({ 
                name: t('selector.costEstimator.regions'), 
                value: totalRegionsCost, 
                color: CHART_COLORS.regions,
                fiat: getFiatStr(totalRegionsCost)
            });
        }

        // --- Use Video pricing as the benchmark for the "approximate" savings note ---
        // This ensures it consistently says ~90% (matching documentation), instead of fluctuating 
        // to 89% for images or text.
        // Formula: ((1 - (Cached / New)) * 100)
        const cachedVid = prices.analysis_cached.video;
        const newVid = prices.analysis_new.video;
        const percentCheaper = Math.round((1 - (cachedVid / newVid)) * 100);

        const exchangeRateText = t('selector.costEstimator.exchangeRateMsg', {
            cost: formatCurrency(1.00 * rate, selectedCurrency, 2)
        });

        return {
            totalUnits,
            totalFiatDisplay,
            chartData,
            exchangeRateText,
            percentCheaper
        };

    }, [pricingConfig, contentType, tasks, includeRegions, includeExplanation, t, selectedCurrency, currencyRates, getTasksLabel]);

    // --- TRIAL USAGE CHECKS ---
    const isMetered = !!user?.stripe_subscription_item_id;
    const isCancellationPending = !!user?.is_cancellation_pending;
    const isGuest = !!user?.is_guest;
    const parseDateSafe = (dateString, fallbackDaysAdd = 0) => {
        if (!dateString) return new Date(new Date().setDate(new Date().getDate() + fallbackDaysAdd));
        let parsed = new Date(dateString);
        if (!isNaN(parsed.getTime())) return parsed;
        if (typeof dateString === 'string') {
            const safeString = dateString.replace(/^(\d{4}-\d{2}-\d{2})\s(\d{2}:\d{2}:\d{2})/, '$1T$2');
            parsed = new Date(safeString);
            if (!isNaN(parsed.getTime())) return parsed;
        }
        return new Date();
    };

    const trialExpires = parseDateSafe(user?.trial_expires_at, 14);
    const daysRemaining = Math.max(0, Math.ceil((trialExpires - new Date()) / (1000 * 60 * 60 * 24)));
    const unitsRemaining = Math.max(0, Number(user?.compute_units_balance || 5000) - Number(user?.trial_spent || 0));

    // --- LOGIC FOR INACTIVE USERS ---
    const hasEverActivatedBilling = !!user?.billing_activated_at;
    const isTrialActive = !isGuest && !isMetered && !hasEverActivatedBilling && daysRemaining > 0 && unitsRemaining > 0;
    const isSubscriptionInactive = !isGuest && !isMetered && hasEverActivatedBilling;
    
    // Show banner for active trials, expired trials, pending cancellations, OR inactive subscriptions
    const showBanner = isTrialActive || isCancellationPending || isSubscriptionInactive || (!isGuest && !isMetered && !hasEverActivatedBilling && (daysRemaining <= 0 || unitsRemaining <= 0));
    
    const trialExhausted = !hasEverActivatedBilling && unitsRemaining <= 0;
    const trialExpired = !hasEverActivatedBilling && daysRemaining <= 0;
    const estimatedCostUnits = costData?.totalUnits || 0;
    const exceedsCredit = isTrialActive && (estimatedCostUnits > unitsRemaining);

    // Push the calculated total units up to the context so ContentInput can gatekeep it
    useEffect(() => {
        if (costData) {
            setEstimatedCostUnits(costData.totalUnits);
        }
    }, [costData, setEstimatedCostUnits]);

    // --- Existing Handlers ---
    const handleOptionChange = (event) => {
        const value = event.target.value;
        setContentType(value);
        setContent('');
        setAnalysisResults({});
    };

    const handleTasksChange = (event) => {
        const { target: { value } } = event;
        // The value from a multi-select is an array of the selected values.
        setTasks(typeof value === 'string' ? value.split(',') : value);
        // setAiScore(null); // Clear previous results on change
        setAnalysisResults({});
    };

    const handleRegionsChange = (event) => {
        setIncludeRegions(event.target.checked);
    }

    const handleExplanationChange = (event) => {
        setIncludeExplanation(event.target.checked);
    }

    // Define the dynamic tooltip text.
    const getRegionsTooltipText = () => {
        if (isRegionsFeatureEnabled) {
            // Build the dynamic key based on the current content type
            const translationKey = `selector.options.inspectableRegions.tooltip_by_type.${contentType}`;
            return t(translationKey);
        } else {
            // Use the disabled key with interpolation
            return t('selector.options.inspectableRegions.tooltip_disabled', {
                supportedTypes: supportedRegionTypes.join(', ')
            });
        }
    };

    const regionsTooltipText = getRegionsTooltipText();

    const explanationTooltipText = t('selector.options.includeExplanation.tooltip');
    const hasError = tasks.length === 0;

    return (

        <div className="selector-container" style={{ position: 'relative' }}>

            {/* Settings Link Box */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: -2, zIndex: 1, position: 'relative' }}>
                <Tooltip title={t('selector.customizePreferencesTooltip')} placement="left" arrow PopperProps={{ className: 'global-dark-tooltip' }}>
                    {/* Pass state to the navigate function */}
                    <IconButton 
                        onClick={() => navigate('/app/account', { state: { scrollToPrefs: true } })} 
                        size="small" 
                        sx={{ color: 'rgba(255,255,255,0.4)', '&:hover': { color: 'rgba(255, 255, 255, 0.8)' }}}
                    >
                        <ManageAccountsIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* --- FormControls for Content Type and Task --- */}
            <FormControl fullWidth variant="outlined" margin="normal">
                {/* ... (Select for Content Type is unchanged) ... */}
                <InputLabel id="content-type-label">{t('selector.contentType.label')}</InputLabel>
                <Select
                    labelId="content-type-label"
                    id="content-type"
                    value={contentType}
                    onChange={handleOptionChange}
                    label={t('selector.contentType.label')}
                >
                    {prefs.displayed_content_types.includes('image') && <MenuItem value="image">{t('selector.contentType.image')}</MenuItem>}
                    {prefs.displayed_content_types.includes('text') && <MenuItem value="text">{t('selector.contentType.text')}</MenuItem>}
                    {prefs.displayed_content_types.includes('video') && <MenuItem value="video">{t('selector.contentType.video')}</MenuItem>}
                    {prefs.displayed_content_types.includes('audio') && <MenuItem value="audio">{t('selector.contentType.audio')}</MenuItem>}
                </Select>
            </FormControl>

            <Box sx={{ position: 'relative', width: '100%', mt: 2, mb: 1 }}>
                
                {/* Floating "Help me choose" button */}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', position: 'absolute', top: -20, right: 0, zIndex: 1 }}>
                    <Button 
                        variant="text" 
                        size="small" 
                        onClick={() => setIsOnboardingModalOpen(true)}
                        sx={{ 
                            color: 'rgba(255,255,255,0.4)', 
                            textTransform: 'none', 
                            p: 0, 
                            fontSize: '0.8rem', 
                            minWidth: 'auto', 
                            '&:hover': { background: 'transparent', color: 'rgba(255,255,255,0.8)' } 
                        }}
                    >
                        {t('selector.helpMeChoose')}
                    </Button>
                </Box>

                <FormControl
                    fullWidth
                    variant="outlined"
                    className={`task-selector-group ${hasError ? 'custom-error-state' : ''}`}
                >
                    <InputLabel id="task-select-label">{t('selector.analysisTasks.label')}</InputLabel>
                    <Select
                        labelId="task-select-label"
                        id="task-multi-select"
                        multiple
                        value={tasks}
                        onChange={handleTasksChange}
                        label={t('selector.analysisTasks.label')}
                        renderValue={(selected) => {
                            if (selected.length === 0) return "";
                            
                            // 1. If backend config has loaded, use the full dynamic list
                            if (availableTasks.length > 0) {
                                const selectedItems = availableTasks.filter(task => selected.includes(task.value));
                                if (selectedItems.length <= 2) {
                                    return selectedItems.map(task => task.label).join(', ');
                                }
                            } else {
                                // 2. FALLBACK: Instantly translate raw keys if backend config is still loading
                                if (selected.length <= 2) {
                                    return selected.map(taskKey => {
                                        const camelCaseKey = taskKey.replace(/_([a-z])/g, (g) => g[1].toUpperCase());
                                        return t(`selector.analysisTasks.${taskKey}`, { 
                                            defaultValue: t(`selector.analysisTasks.${camelCaseKey}`, { defaultValue: taskKey }) 
                                        });
                                    }).join(', ');
                                }
                            }
                            
                            // 3. Aggregate label (e.g. "3 Tasks Selected")
                            const count = selected.length;
                            const i18nKey = 'selector.analysisTasks.selectedSummary';
                            const translated = t(i18nKey, { count });
                            if (translated !== i18nKey) return translated;
                            return `${count} ${count === 1 ? 'Task' : 'Tasks'} Selected`;
                        }}
                        MenuProps={{ className: 'multi-select-menu' }}
                    >
                        {availableTasks.map((task) => (
                            <MenuItem 
                                key={task.value} 
                                value={task.value} 
                                sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                    <Checkbox checked={tasks.indexOf(task.value) > -1} />
                                    <ListItemText primary={task.label} sx={{ m: 0 }} />
                                </Box>
                                
                                <Tooltip 
                                    title={task.description} 
                                    arrow 
                                    placement="right"
                                    PopperProps={{ 
                                        className: 'global-dark-tooltip',
                                        sx: { zIndex: 9999 }
                                    }}
                                >
                                    <Box 
                                        onClick={(e) => e.stopPropagation()} 
                                        onKeyDown={(e) => e.stopPropagation()}
                                        sx={{ display: 'flex', alignItems: 'center', p: 0.5, ml: 1, cursor: 'help' }}
                                    >
                                        <InfoIcon sx={{ fontSize: '1.1rem', color: 'rgba(0,0,0,0.4)', '&:hover': { color: 'rgba(0,0,0,0.8)' } }} />
                                    </Box>
                                </Tooltip>
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {/* --- Explanation Control with Custom Switch --- */}
            {prefs.display_explanation && (
                <div className="explanation-control-container">
                    <Tooltip
                        title={explanationTooltipText}
                        arrow
                        placement="left"
                        // --- MODIFICATION: Apply global dark tooltip style ---
                        PopperProps={{ className: 'global-dark-tooltip' }}
                    >
                        <IconButton size="small" className="info-icon-button">
                            <InfoIcon fontSize="small" className="custom-info-icon" />
                        </IconButton>
                    </Tooltip>

                    {/* ... (Switch and Typography for Explanation are unchanged) ... */}
                    <Switch
                        className="custom-explanation-switch"
                        checked={includeExplanation && isExplanationFeatureEnabled}
                        onChange={handleExplanationChange}
                        name="explanationSwitch"
                        id="explanation-switch"
                        disabled={!isExplanationFeatureEnabled}
                        inputProps={{ 'aria-label': t('selector.options.includeExplanation.label') }}
                    />
                    <div className="explanation-label">
                        <Typography component="span" className="explanation-main-text" style={{ opacity: isExplanationFeatureEnabled ? 1 : 0.5 }}>
                            {t('selector.options.includeExplanation.label')}
                        </Typography>
                        <Typography component="p" className="explanation-subtext" style={{ opacity: isExplanationFeatureEnabled ? 1 : 0.5 }}>
                            {t('selector.options.includeExplanation.subtext')}
                        </Typography>
                    </div>
                </div>
            )}

            {/* --- Switch for Inspectable Regions --- */}
            {prefs.display_regions && (
                <div className="explanation-control-container">

                    <Tooltip
                        title={regionsTooltipText}
                        arrow
                        placement="left"
                        PopperProps={{ className: 'global-dark-tooltip' }}
                    >
                        <IconButton size="small" className="info-icon-button">
                            <InfoIcon fontSize="small" className="custom-info-icon" />
                        </IconButton>
                    </Tooltip>

                    <Switch
                        className="custom-explanation-switch"
                        checked={includeRegions && isRegionsFeatureEnabled}
                        onChange={handleRegionsChange}
                        name="regionsSwitch"
                        id="regions-switch"
                        disabled={!isRegionsFeatureEnabled}
                        inputProps={{ 'aria-label': t('selector.options.inspectableRegions.label') }}
                    />

                    <div className="explanation-label">
                        <Typography
                            component="span"
                            className="explanation-main-text"
                            style={{ opacity: isRegionsFeatureEnabled ? 1 : 0.5 }}
                        >
                            {t('selector.options.inspectableRegions.label')}
                        </Typography>

                        <Typography
                            component="p"
                            className="explanation-subtext"
                            style={{ opacity: isRegionsFeatureEnabled ? 1 : 0.5 }}
                        >
                            {t('selector.options.inspectableRegions.subtext')}
                        </Typography>
                    </div>
                </div>

            )}

            {/* --- Cost Estimator Accordion --- */}
            {costData && (
                <Accordion 
                    className="cost-estimator-accordion" 
                    expanded={isCostExpanded} 
                    onChange={(e, expanded) => setIsCostExpanded(expanded)}
                    sx={{ position: 'relative' }}
                >
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon sx={{ color: 'rgba(255,255,255,0.7)' }} />}
                        aria-controls="cost-content"
                        id="cost-header"
                    >
                        {/* Left Side: Title & Subtitle */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography className="explanation-main-text">
                                    {t('selector.costEstimator.title')}
                                </Typography>
                                {/* Help Button */}
                                <Tooltip 
                                    title={t('selector.costEstimator.helpTooltip')} 
                                    arrow 
                                    placement="top"
                                    PopperProps={{ className: 'global-dark-tooltip' }}
                                >
                                    <Box 
                                        component="span"
                                        className="cost-help-btn"
                                        onClick={(e) => {
                                            e.stopPropagation(); 
                                            openDocViewer('business/billing_and_usage_units.md');
                                        }}
                                    >
                                        <HelpOutlineIcon fontSize="inherit" />
                                    </Box>
                                </Tooltip>
                            </Box>
                            
                            <Typography className="explanation-subtext cost-breakdown-subtext">
                                {t('selector.costEstimator.subtitle', { 
                                    units: costData.totalUnits, 
                                    cost: costData.totalFiatDisplay 
                                })}
                            </Typography>
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails>
                        {/* Right Side: Currency Dropdown */}
                        {/* FIX: Moved Currency Dropdown here for Accessibility */}
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mb: 2 }} className="cost-estimator-currency-row">
                            <Tooltip 
                                title={t('billingPage.exchangeRateTooltip')} 
                                arrow 
                                placement="left"
                                PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <InfoIcon className="currency-info-icon" fontSize="small" />
                            </Tooltip>
                            <Select
                                value={selectedCurrency}
                                onChange={(e) => setSelectedCurrency(e.target.value)}
                                variant="standard"
                                disableUnderline
                                size="small"
                                className="currency-selector-small"
                                inputProps={{ 'aria-label': 'Change Currency' }}
                                MenuProps={{ className: 'currency-menu' }}
                            >
                                {Object.keys(currencyRates).map(curr => (
                                    <MenuItem key={curr} value={curr}>
                                        {curr} ({CURRENCY_SYMBOLS[curr] || curr})
                                    </MenuItem>
                                ))}
                            </Select>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            {/* Chart Side */}
                            <Box sx={{ width: '120px', height: '120px' }}>
                                <ResponsiveContainer>
                                    <PieChart>
                                        <Pie
                                            data={costData.chartData}
                                            cx="50%" cy="50%"
                                            innerRadius={30} outerRadius={50}
                                            paddingAngle={5}
                                            dataKey="value"
                                            stroke="none"
                                        >
                                            {costData.chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <RechartsTooltip 
                                            contentStyle={{ 
                                                backgroundColor: 'var(--primary-color)', 
                                                border: '1px solid rgba(255, 255, 255, 0.2)', 
                                                borderRadius: '4px', 
                                                fontSize: '12px',
                                                padding: '8px 12px'
                                            }}
                                            itemStyle={{ color: '#fff', padding: 0 }}
                                            formatter={(value, name, props) => {
                                                const percent = ((value / costData.totalUnits) * 100).toFixed(0);
                                                // Return a React Fragment for multi-line support
                                                return [
                                                    <div key="tooltip-content" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                        <span style={{ fontWeight: 600, color: '#fff' }}>{name}</span>
                                                        <span style={{ color: 'rgba(255,255,255,0.7)' }}>
                                                            {percent}% - {value} {t('selector.costEstimator.units')} <span style={{ color: 'rgba(255,255,255,0.7)' }}>({props.payload.fiat})</span>
                                                        </span>
                                                    </div>, 
                                                    '' // Empty name to hide the default label
                                                ];
                                            }}
                                            separator="" 
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>
                            
                            {/* Legend Side */}
                            <Box sx={{ flex: 1, ml: 3 }}>
                                {costData.chartData.map((item, index) => (
                                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1, justifyContent: 'space-between' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: item.color, mr: 1.5 }} />
                                            <Typography sx={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.8)' }}>
                                                {item.name}
                                            </Typography>
                                        </Box>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 'bold', color: '#fff' }}>
                                            {item.value} <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'normal' }}>({item.fiat})</span>
                                        </Typography>
                                    </Box>
                                ))}
                                <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', justifyContent: 'space-between', flexDirection: 'column' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                        <Typography sx={{ fontSize: '0.8rem', fontWeight: 600, color: 'white' }}>
                                            {t('selector.costEstimator.total')}
                                        </Typography>
                                        {/* Total fiat amount colored gray */}
                                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff' }}>
                                            {costData.totalUnits} {t('selector.costEstimator.units')} <span style={{ color: 'rgba(255,255,255,0.6)', fontWeight: 'normal', fontSize: '0.85rem' }}>({costData.totalFiatDisplay})</span>
                                        </Typography>
                                    </Box>
                                    <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textAlign: 'right', mt: 0.5 }}>
                                        {costData.exchangeRateText}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        {/* Footer Note about Cached Analysis */}
                        <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.6)', fontStyle: 'italic', textAlign: 'center' }}>
                                {t('selector.costEstimator.cacheNote', { percent: costData.percentCheaper })}
                            </Typography>
                        </Box>
                    </AccordionDetails>
                </Accordion>
            )}
            {/* --- TRIAL MODE BANNER --- */}
            {showBanner && (
                <Box sx={{ width: '100%', mt: 2 }}>
                    <Alert 
                        severity={(trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? "error" : "info"}
                        action={
                            <Button 
                                component={RouterLink} 
                                to="/app/billing" 
                                color="inherit" 
                                size="small"
                                variant="outlined"
                                startIcon={<RocketLaunchIcon />}
                                sx={{ 
                                    fontWeight: 'bold', 
                                    whiteSpace: 'nowrap', 
                                    height: 'fit-content' 
                                }}
                            >
                                {(isCancellationPending || isSubscriptionInactive)
                                    ? t('appContent.trialBanner.reactivateBtn') 
                                    : t('appContent.trialBanner.upgradeBtn')
                                }
                            </Button>
                        }
                        sx={{
                            alignItems: 'center',
                            backgroundColor: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? 'rgba(211, 47, 47, 0.1)' : 'rgba(9, 132, 227, 0.1)',
                            color: 'white',
                            border: '1px solid',
                            borderColor: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? 'rgba(211, 47, 47, 0.5)' : 'rgba(9, 132, 227, 0.5)',
                            '& .MuiAlert-icon': {
                                color: (trialExhausted || trialExpired || exceedsCredit || isCancellationPending || isSubscriptionInactive) ? '#ef5350' : '#74b9ff',
                                padding: 0,
                                marginRight: '12px'
                            },
                            '& .MuiAlert-message': {
                                width: '100%',
                                maxWidth: '70%',
                                fontSize: '0.9rem',
                                lineHeight: 1.4,
                                padding: '4px 0'
                            },
                            '& .MuiAlert-action': {
                                padding: '0 0 0 16px',
                                margin: 0,
                                display: 'flex',
                                alignItems: 'center'
                            }
                        }}
                    >
                        {isCancellationPending ? (
                            <strong>{t('appContent.trialBanner.cancellationPending')}</strong>
                        ) : isSubscriptionInactive ? (
                            <strong>{t('appContent.trialBanner.subscriptionInactive', 'Subscription Inactive: Please add a valid payment method to resume access.')}</strong>
                        ) : trialExpired ? (
                            <strong>{t('appContent.trialBanner.expired')}</strong>
                        ) : trialExhausted ? (
                            <strong>{t('appContent.trialBanner.exhausted')}</strong>
                        ) : exceedsCredit ? (
                            <strong>{t('appContent.trialBanner.exceeds', { 
                                estimated: Math.round(estimatedCostUnits).toLocaleString(), 
                                remaining: Math.round(unitsRemaining).toLocaleString() 
                            })}</strong>
                        ) : (
                            <span>
                                {t('appContent.trialBanner.active', { 
                                    units: Math.round(unitsRemaining).toLocaleString(), 
                                    days: daysRemaining,
                                    defaultValue: `Trial Mode: ${Math.round(unitsRemaining).toLocaleString()} Units and ${daysRemaining} Days remaining.`
                                })}
                            </span>
                        )}
                    </Alert>
                </Box>
            )}
        </div>
    );
}

export default Selector;